import { afterEveryRender, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ClientLinkOrganization, UserClientLink } from '../../core/services/UserClientLinkService';
import * as A from '../../core/client-links/client-links.reducer';

let instance = 0;
@Component({
  standalone: false,
  selector: 'sam-client-links',
  host: { '[class.embedded]': 'embedded' },
  templateUrl: './client-links.component.html',
  styleUrls: ['./client-links.component.scss']
})
export class ClientLinksComponent implements OnChanges, OnDestroy {
  @Input() userId: string;
  @Input() embedded = false;
  scope = 'client-links-' + (++instance);
  state = { ...A.emptyLinks };
  expanded = false;
  editing = false;
  savedOrganizationId: number | null = null;
  term = '';
  searched = false;
  organization: ClientLinkOrganization | null = null;
  externalId = '';
  editId: number | null = null;
  pendingDelete: number | null = null;
  private request = 0;
  private destroyed = new Subject<void>();
  private focusField: 'search' | 'id' | null = null;
  private revealOrganizationId: number | null = null;
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('idInput') idInput?: ElementRef<HTMLInputElement>;
  @ViewChildren('linkRow') linkRows?: QueryList<ElementRef<HTMLElement>>;

  constructor(private store: Store<any>, actions: Actions, private changeDetectorRef: ChangeDetectorRef) {
    afterEveryRender(() => this.updateFocus());
    store.select(s => s.clientLinks?.[this.scope]).pipe(takeUntil(this.destroyed)).subscribe(state => {
      this.state = state || { ...A.emptyLinks };
      this.changeDetectorRef.markForCheck();
    });
    actions.pipe(ofType(A.linksSaved), takeUntil(this.destroyed)).subscribe(a => {
      if (a.scope === this.scope) {
        this.reset();
        this.savedOrganizationId = a.organizationId ?? null;
        this.revealOrganizationId = a.organizationId ?? null;
        this.changeDetectorRef.markForCheck();
      }
    });
  }
  ngOnChanges() {
    this.reset();
    this.savedOrganizationId = null;
    this.revealOrganizationId = null;
    this.store.dispatch(A.linksClose({ scope: this.scope }));
    this.scope = 'client-links-' + (++instance);
    if (this.userId) this.store.dispatch(A.linksOpen({ scope: this.scope, userId: this.userId }));
  }
  reload() { this.store.dispatch(A.linksLoad({ scope: this.scope, userId: this.userId })); }
  startAdd() {
    if (this.state.busy) return;
    this.reset();
    this.editing = true;
    this.savedOrganizationId = null;
    this.revealOrganizationId = null;
    this.focusField = 'search';
  }
  search() {
    this.searched = true;
    this.store.dispatch(A.linksSearch({ scope: this.scope, userId: this.userId, term: this.term.trim(), request: ++this.request }));
  }
  selectOrganization(organization: ClientLinkOrganization) {
    if (this.state.busy) return;
    this.organization = organization;
    this.focusField = 'id';
  }
  changeOrganization() {
    if (this.state.busy) return;
    this.organization = null;
    this.term = '';
    this.searched = false;
    this.focusField = 'search';
  }
  edit(link: UserClientLink) {
    if (this.state.busy) return;
    this.startAdd();
    this.editId = link.Id;
    this.organization = { Id: link.OrganizationId, Name: link.OrganizationName, OrgNumber: '' };
    this.externalId = link.ExternalId;
    this.pendingDelete = null;
    this.focusField = 'id';
  }
  save() {
    if (!this.organization || !this.externalId.trim() || this.externalId.trim().length > 120 || this.state.busy) return;
    this.store.dispatch(A.linksSave({ scope: this.scope, userId: this.userId, id: this.editId,
      input: { OrganizationId: this.organization.Id, ExternalId: this.externalId.trim() } }));
  }
  remove(id: number) {
    if (this.state.busy) return;
    this.store.dispatch(A.linksDelete({ scope: this.scope, userId: this.userId, id }));
  }
  reset() {
    this.editing = false;
    this.editId = null;
    this.organization = null;
    this.externalId = '';
    this.pendingDelete = null;
    this.term = '';
    this.searched = false;
    this.focusField = null;
  }
  private updateFocus() {
    if (!this.embedded && !this.expanded) return;
    const input = this.focusField === 'search' ? this.searchInput : this.focusField === 'id' ? this.idInput : null;
    if (input) {
      this.focusField = null;
      input.nativeElement.focus({ preventScroll: true });
      input.nativeElement.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    if (this.revealOrganizationId === null || this.state.busy || this.state.loading || this.state.error) return;
    const row = this.linkRows?.find(item => item.nativeElement.getAttribute('data-organization-id') === String(this.revealOrganizationId));
    if (row) {
      this.revealOrganizationId = null;
      row.nativeElement.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      row.nativeElement.focus({ preventScroll: true });
    }
  }
  ngOnDestroy() {
    this.destroyed.next(); this.destroyed.complete();
    this.store.dispatch(A.linksClose({ scope: this.scope }));
  }
}
